import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIResponseWrapper } from "../../models/common/api-response-wrapper";
import { Vehicle } from "../vehicle/vehicle.model";
import { Driver } from "./driver.model";
import { APIListWrapper } from "../../models/common/api-list-wrapper.model";
import { environment } from "../../../environments/environment";
import { AuthService } from "../../security/auth.service";

export interface RentalResourceTerms {
    vendorName?: string;
    vendorCompanyId?: number | null;
    contractReferenceNumber?: string;
    rentalStartDate?: number | Date | null;
    rentalEndDate?: number | Date | null;
    rentalRateAmount?: number | null;
    rentalRateCurrency?: string;
    rentalRatePeriod?: string;
    depositAmount?: number | null;
    depositCurrency?: string;
    renewalStatus?: string;
    billId?: number | null;
    expenseId?: number | null;
    notes?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DriverService {

    private baseUrl = `${environment.restApiUrl}driver-endpoint`; // Adjust the base URL if needed

    constructor(private http: HttpClient, private authService: AuthService) { }

    findDriverByFilter(
        driverId?: string,
        driverDatabaseId?: number,
        emailAddress?: string,
        phoneNumber?: string,
        vehicleInfoRequired?: boolean,
        cultureCode?: string,
        companyId?: number,
        companyGroupId?: number
    ) {
        const params: any = {}; // Start with an empty object

        // Conditionally add parameters if they have values
        if (driverId) {
            params.driverId = driverId;
        }
        if (driverDatabaseId) {
            params.driverDatabaseId = driverDatabaseId;
        }
        if (emailAddress) {
            params.emailAddress = emailAddress;
        }
        if (phoneNumber) {
            params.phoneNumber = phoneNumber;
        }
        if (vehicleInfoRequired !== undefined) {
            params.vehicleInfoRequired = vehicleInfoRequired.toString();
        }
        if (cultureCode) {
            params.cultureCode = cultureCode;
        }
        if (companyId != null) {
            params.companyId = companyId.toString();
        }
        if (companyGroupId != null) {
            params.companyGroupId = companyGroupId.toString();
        }
        return this.http.get<APIResponseWrapper<Driver>>(`${this.baseUrl}/driver`, { params });
    }

    findVehiclesByDriver(driverId: string, cultureCode: string) {
        const params = { driverId, cultureCode };
        return this.http.get<Vehicle[]>(`${this.baseUrl}/vehicles`, { params });
    }

    addDriver(driver: Driver) {
        return this.http.post<APIResponseWrapper<Driver>>(`${this.baseUrl}/driver`, driver);
    }

    updateDriver(driver: Driver) {
        return this.http.put<APIResponseWrapper<Driver>>(`${this.baseUrl}/driver`, driver);
    }

    updateCurrentVehicleForDriver(driverId: string, vehicleId: number) {
        const params = { driverId, vehicleId: vehicleId.toString() };
        return this.http.put<APIResponseWrapper<Driver>>(`${this.baseUrl}/driver-current-vehicle`, null, { params });
    }

    deleteDriver(driver: Driver) {
        return this.http.delete<APIResponseWrapper<Driver>>(`${this.baseUrl}/driver`, { body: driver });
    }

    findDriversByTransportationAgencyId(page: number, pageSize: number, transportationAgencyId: number) {
        const params = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            transportationAgencyId: transportationAgencyId.toString()
        };
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/drivers-by-transportation-agency`, { params });
    }

    findDriversByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/drivers-by-company-group`, { params });
    }

    getFreelanceDriversByCompanyId(page: number, pageSize: number, companyId: number) {
        const params = { page: page.toString(), pageSize: pageSize.toString(), companyId: companyId.toString() };
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/freelance-drivers-by-company`, { params });
    }

    getFreelanceDriversByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/freelance-drivers-by-company-group`, { params });
    }

    addFreelanceDriver(driver: Driver, companyId: number) {
        const url = `${this.baseUrl}/freelance-driver?companyId=${companyId}`;
        return this.http.post<APIResponseWrapper<boolean>>(url, driver);
    }

    getTransporterDrivers(page: number, pageSize: number, companyId: number, transporterCrmClientId: number) {
        const params = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyId: companyId.toString(),
            transporterCrmClientId: transporterCrmClientId.toString()
        };
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/transporter-drivers`, { params });
    }

    addTransporterDriver(driver: Driver, companyId: number) {
        return this.http.post<APIResponseWrapper<Driver>>(`${this.baseUrl}/transporter-driver?companyId=${companyId}`, driver);
    }

    getRentalDriversByCompanyId(page: number, pageSize: number, companyId: number) {
        const params = { page: page.toString(), pageSize: pageSize.toString(), companyId: companyId.toString() };
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/rental-drivers-by-company`, { params });
    }

    getRentalDriversByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Driver>>(`${this.baseUrl}/rental-drivers-by-company-group`, { params });
    }

    addRentalDriver(driver: Driver, companyId: number, terms: RentalResourceTerms) {
        const url = `${this.baseUrl}/rental-driver?companyId=${companyId}`;
        return this.http.post<APIResponseWrapper<boolean>>(url, { driver, ...terms });
    }

    deactivateRentalDriver(driverId: number, companyId: number) {
        const params = { driverId: driverId.toString(), companyId: companyId.toString() };
        return this.http.delete<APIResponseWrapper<boolean>>(`${this.baseUrl}/rental-driver`, { params });
    }
}
